import React, { useEffect, useMemo, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import {
  formatIsoDate,
  formatName,
  isFsrSpectatablePosition,
} from "../../utils/generalUtils";
import { Select, Space, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import SpectatorTable from "../../components/Table/SpectatorTable.js";
import usePdfBlob from "../../hooks/usePdfBlob.js";
import PdfPreviewer from "../../components/PdfPreviewer.js";
import { renderFsrStatus } from "../../utils/fsrApprovalUtils";

const FsrPdfViewer = ({ data }) => {
  const { pdfBlobUrl, loading } = usePdfBlob({
    endpoint: "/fsr/pdf/snapshot",
    params: { submission_id: data.submission_id },
    enabled: !!data?.submission_id,
  });
  return <PdfPreviewer pdfBlobUrl={pdfBlobUrl} loading={loading} height="400px" />;
};

const SpectateFsr = () => {
  const { response, loading, fetchData } = useAxios();
  const { spectatorFsrPopup } = useSelector((state) => state.generalSlice);
  const { spectator_accesses } = useSelector(
    (state) => state.accountDetailSlice.details,
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/fsr/${positionId}`, method: "GET" }).catch(() => {});
  };

  const mappedEmpPos = useMemo(
    () => (spectator_accesses || [])
      // Only Dean/Program Chair positions under one of the 3 faculties can
      // be spectated for FSR — an AO's access to a non-Dean/Chair position,
      // or a Dean/Chair-titled position outside FEd/FICS/FMDS, doesn't
      // apply here, even though it still applies to TRL/COS/CWA. A Dean
      // grant scoped "ao" (AO View) is deliberately excluded too — that
      // scope means TRL/COS/CWA only.
      .filter(
        (item) =>
          isFsrSpectatablePosition(item?.approver_position) &&
          item?.scope !== "ao"
      )
      .map((item) => ({
        value: item?.approver_position_id,
        label: item?.approver_position
          ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
          : `Position ${item?.approver_position_id}`,
      })),
    [spectator_accesses],
  );
  const hasActiveSpectatorPosition = mappedEmpPos.length > 0;

  // Starts undefined (rather than defaulting to spectator_accesses[0]) since
  // the raw list isn't faculty-filtered — the effect below picks the first
  // *filtered* position from mappedEmpPos once it's computed.
  const [selectedPos, setSelectedPos] = useState(undefined);

  useEffect(() => {
    if (selectedPos == null && mappedEmpPos.length > 0) {
      setSelectedPos(mappedEmpPos[0].value);
    }
  }, [mappedEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut",
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut",
      ),
      ...item?.submission?.faculty_service_record,
      all_approvals: item?.submission?.approval || [],
      employee_id: item?.submission?.employee_id,
      position_id: item?.submission?.position_id,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      submitter_position: item?.submission?.position?.position_name,
      remarks: item?.remarks || null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key",
      rowScope: "row",
      width: 60,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Academic Term",
      dataIndex: "academic_term",
      key: "academic_term",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderFsrStatus,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (value) => (value ? value : "—"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return <Button icon={<EyeOutlined />} type="primary" shape="circle" />;
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ];

  return (
    <>
      {loading && "loading..."}
      {!loading && (
        <>
          <Space>
            <p>Spectate: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveSpectatorPosition}
              popupMatchSelectWidth={false}
            />
          </Space>
          {!hasActiveSpectatorPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active spectator position available.
            </p>
          )}
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={spectatorFsrPopup}
            popupName="spectatorFsrPopup"
            popupWidth={1000}
            docuType="Faculty Service Record"
            addtnlContent={(data) => <FsrPdfViewer data={data} />}
          />
        </>
      )}
    </>
  );
};

export default SpectateFsr;
